import type { InferRpcDefinition } from "@redotech/rpc/definition";
import { createCustomerProductList } from "./schema/accounts/create-customer-product-list";
import { getCustomerProductList } from "./schema/accounts/get-customer-product-list";
import { addAiReturnReasonInput } from "./schema/add-ai-return-reason-input";
import { addCustomerDiscountCodes } from "./schema/add-customer-discount-codes";
import { addDynamicMerchantItemFlow } from "./schema/add-dynamic-merchant-item-flow";
import { addMerchantOverrides } from "./schema/add-merchant-overrides";
import { addMobileUploadImage } from "./schema/add-mobile-upload-image";
import { addReturnItem } from "./schema/add-return-item";
import { getAddressFromSuggestion } from "./schema/address-autofill/get-address-from-suggestion";
import { getAutocompleteSuggestions } from "./schema/address-autofill/get-autocomplete-suggestions";
import { calculateNewOrderFromReturn } from "./schema/calculate-new-order-from-return";
import { cancelReturn } from "./schema/cancel-return";
import { changeResetPoint } from "./schema/change-reset-point";
import { classifyClaimImage } from "./schema/classify-claim-image";
import { cloneDraftReturnExcludingItems } from "./schema/clone-draft-return-excluding-items";
import { completeExchangeConversion } from "./schema/complete-exchange-conversion";
import { completeMobileUpload } from "./schema/complete-mobile-upload";
import { convertBoxlessTokenToCustomerAuth } from "./schema/convert-boxless-token-to-customer-auth";
import { convertTokenToCustomerAuth } from "./schema/convert-token-to-customer-auth";
import {
  addProductToOrderlessDraftReturn,
  createDraftReturn,
  createOrderlessDraftReturn,
} from "./schema/create-draft-return";
import { createDraftReturnFromSource } from "./schema/create-draft-return-from-source";
import { createManualDiscount } from "./schema/create-manual-discount";
import { createMobileUploadToken } from "./schema/create-mobile-upload-token";
import { getDropoffLocations } from "./schema/dropoffs/get-dropoff-locations";
import { getAdvancedExchangeRecommendation } from "./schema/exchange-agent/get-advanced-exchange-recommendation";
import { getLocationFilteredInventory } from "./schema/exchange-agent/get-location-filtered-inventory";
import { getMerchantRecommendedExchangeProducts } from "./schema/exchange-agent/get-merchant-recommended-exchange-products";
import { getPersonalizedProductExchangeRecommendations } from "./schema/exchange-agent/get-personalized-product-exchange-recommendations";
import { getPersonalizedRecommendationsStream } from "./schema/exchange-agent/get-personalized-recommendations-stream";
import { getReturnReasonRecommendationsStream } from "./schema/exchange-agent/get-return-reason-recommendations";
import { getVariantExchangeRecommendations } from "./schema/exchange-agent/get-variant-exchange-recommendations";
import { warmExchangeAgentCaches } from "./schema/exchange-agent/warm-exchange-agent-caches";
import { warmExchangeAgentCachesV2 } from "./schema/exchange-agent/warm-exchange-agent-caches-v2";
import { generateDiscountCode } from "./schema/games/generate-discount-code";
import { getAiReturnReasonsForProduct } from "./schema/get-ai-return-reasons-for-product";
import { getAllowedOrderEditTypes } from "./schema/get-allowed-order-edit-types";
import { getBlueYonderLocations } from "./schema/get-blue-yonder-locations";
import { getCarrierLocations } from "./schema/get-carrier-locations";
import { getCartDetails } from "./schema/get-cart-details";
import { getExchangeInvoice } from "./schema/get-exchange-invoice";
import { getMobileUploadResult } from "./schema/get-mobile-upload-result";
import { getOrderById } from "./schema/get-order-by-id";
import { getOrderRegistrationEligibility } from "./schema/get-order-registration-eligibility";
import { getOrdersFromSearch } from "./schema/get-orders-from-search";
import { getProduct } from "./schema/get-product";
import { getProductFilterById } from "./schema/get-product-filter-by-id";
import { getReturn } from "./schema/get-return";
import { getReturnCreationStatus } from "./schema/get-return-creation-status";
import { getReturnTotals } from "./schema/get-return-totals";
import { getReturnTotalsFeatureFlag } from "./schema/get-return-totals-feature-flag";
import { getReturnsByOrderId } from "./schema/get-returns-by-order-id";
import { getSharedList } from "./schema/get-shared-list";
import { getShippingMethodsSchema } from "./schema/get-shipping-methods";
import { getShopifyLocation } from "./schema/get-shopify-location";
import { getSubmissionWorkflowStatus } from "./schema/get-submission-workflow-status";
import { getTrackingPage } from "./schema/get-tracking-page";
import { getTrackingPageOrder } from "./schema/get-tracking-page-order";
import { giftAuth } from "./schema/gift-auth";
import { giftReceiptAuth } from "./schema/gift-receipt-auth";
import { createGiftReceiptRequest } from "./schema/gift-receipt-requests/create-gift-receipt-request";
import { createGiftReceipt } from "./schema/gift-receipts/create-gift-receipt";
import { deleteGiftReceipt } from "./schema/gift-receipts/delete-gift-receipt";
import { getGiftReceipt } from "./schema/gift-receipts/get-gift-receipt";
import { getGiftReceiptCount } from "./schema/gift-receipts/get-gift-receipt-count";
import { getGiftReceipts } from "./schema/gift-receipts/get-gift-receipts";
import { sendGiftReceipt } from "./schema/gift-receipts/send-gift-receipt";
import { hasMultipleOrders } from "./schema/has-multiple-orders";
import { hasRefundidIntegration } from "./schema/has-refundid-integration";
import { hasReturnableItems } from "./schema/has-returnable-items";
import {
  getSignupForm,
  handleMarketingEmailUnsubscribe,
  logSignupFormAnalytics,
  submitSignupFormStep,
} from "./schema/marketing";
import { checkCustomerSubscription } from "./schema/marketing/check-customer-subscription";
import { createSubscription } from "./schema/marketing/create-subscription";
import { getCustomFieldOptions } from "./schema/marketing/get-custom-field-options";
import { getSubscriptionPlans } from "./schema/marketing/get-subscription-plans";
import { getFulfillmentGroupAddress } from "./schema/oms/get-fulfillment-group-address";
import { updateFulfillmentGroupAddress } from "./schema/oms/update-fulfillment-group-address";
import { cancelOrderSelfServe } from "./schema/order-editing/cancel-order-self-serve";
import { getBestSellers } from "./schema/order-editing/get-best-sellers";
import { lookupProductsByIds } from "./schema/order-editing/lookup-products-by-ids";
import { searchProducts } from "./schema/order-editing/search-products";
import { updateAddressSelfServe } from "./schema/order-editing/update-address-self-serve";
import { updateContactInfoSelfServe } from "./schema/order-editing/update-contact-info-self-serve";
import { updateDiscountCodeSelfServe } from "./schema/order-editing/update-discount-code-self-serve";
import { updateOrderSelfServe } from "./schema/order-editing/update-order-self-serve";
import { orderlessAuth } from "./schema/orderless-auth";
import { getReturnBearLocations } from "./schema/returnbear/get-returnbear-locations";
import {
  addNewItems,
  calculateReturnTotals,
  calculateReturnTotalsForReturn,
  cancelPickup,
  changeAddress,
  editPendingItemQuantity,
  editReturnableItem,
  getBigCommerceUpsellRecommendedProducts,
  getCommerceCloudUpsellRecommendedProducts,
  getDraftReturn,
  getExchangeShippingOptions,
  getFaq,
  getFaqPage,
  getLocations,
  getMarketingTriggered,
  getOrderId,
  getOrders,
  getRecommendedProductsByProvider,
  getRegistrationDiscounts,
  getRegistrations,
  getSatisfactionResponse,
  getShippingEstimate,
  getStripePublicKey,
  getWarrantyEligibleProducts,
  getWarrantyRegistration,
  getWarrantyRegistrationFlow,
  isShopAppDisabled,
  rejectReturnItems,
  removeNewItems,
  removeReturnItem,
  reschedulePickup,
  saveSatisfactionResponse,
  searchExchangeProducts,
  setCompensationMethod,
  setExchangeShippingOption,
  setProvisionType,
  setShipmentMethodInfo,
  submitWarrantyRegistration,
  updatePickupVariant,
  verifyAddress,
} from "./schema/schemas";
import { selectReturnItems } from "./schema/select-return-items";
import { sendEmailAuthLink } from "./schema/send-email-auth-link";
import { setThirdPartyRefundProvider } from "./schema/set-third-party-refund-provider";
import { shopAppTempLogin } from "./schema/shop-app-temp-login";
import { singleOrderAuth } from "./schema/single-order-auth";
import { skipAddReturnItems } from "./schema/skip-add-return-items";
import { convertSocialAuthToCustomerAuth } from "./schema/social-auth";
import { submitDraftReturn } from "./schema/submit-draft-return";
import { subscribeToTrackingUpdates } from "./schema/subscribe-to-tracking-updates";
import { getTnplContractForOrder } from "./schema/tnpl/get-order-tnpl-contract";
import { updateReturnExchangeAddress } from "./schema/update-return-exchange-address";

// Split up the RPC schemas to prevent the RPC definition type from getting too large for TypeScript to emit

const changeAddressRpcSchema = { changeAddress };
const editReturnableItemRpcSchema = { editReturnableItem };
const createDraftReturnRpcSchema = {
  createDraftReturn,
  createOrderlessDraftReturn,
  addProductToOrderlessDraftReturn,
  createDraftReturnFromSource,
};
const shopAppTempLoginSchema = { shopAppTempLogin };
const editPendingItemQuantityRpcSchema = { editPendingItemQuantity };
const getDraftReturnRpcSchema = { getDraftReturn };
const getShippingEstimateRpcSchema = { getShippingEstimate };
const getRecommendedProductsByProviderRpcSchema = {
  getRecommendedProductsByProvider,
};
const getCommerceCloudUpsellRecommendedProductsRpcSchema = {
  getCommerceCloudUpsellRecommendedProducts,
};
const getBigCommerceUpsellRecommendedProductsRpcSchema = {
  getBigCommerceUpsellRecommendedProducts,
};
const searchExchangeProductsRpcSchema = { searchExchangeProducts };
const getStripePublicKeyRpcSchema = { getStripePublicKey };
const setShipmentMethodInfoRpcSchema = { setShipmentMethodInfo };
const setCompensationMethodRpcSchema = { setCompensationMethod };
const addDynamicMerchantItemFlowRpcSchema = { addDynamicMerchantItemFlow };
const addAiReturnReasonInputRpcSchema = { addAiReturnReasonInput };
const addReturnItemRpcSchema = { addReturnItem };
const skipAddReturnItemsRpcSchema = { skipAddReturnItems };
const removeReturnItemRpcSchema = { removeReturnItem };
const submitDraftReturnRpcSchema = { submitDraftReturn };
const rejectReturnItemsRpcSchema = { rejectReturnItems };
const addNewItemsRpcSchema = { addNewItems };
const removeNewItemsRpcSchema = { removeNewItems };
const verifyAddressRpcSchema = { verifyAddress };
const calculateReturnTotalsRpcSchema = { calculateReturnTotals };
const cancelPickupSchema = { cancelPickup };
const cancelReturnSchema = { cancelReturn };
const updateReturnExchangeAddressSchema = { updateReturnExchangeAddress };
const reschedulePickupSchema = { reschedulePickup };

const getLocationsRpcSchema = { getLocations };
const getBlueYonderLocationsRpcSchema = { getBlueYonderLocations };
const getCarrierLocationsRpcSchema = { getCarrierLocations };
const getDropoffLocationsSchema = { getDropoffLocations };
const getSatisfactionResponseRpcSchema = { getSatisfactionResponse };
const saveSatisfactionResponseSchema = { saveSatisfactionResponse };
const getFaqSchema = { getFaq, getFaqPage };
const getReturnBearLocationsSchema = { getReturnBearLocations };

const selectReturnItemsSchema = { selectReturnItems };
const getSharedListRpcSchema = { getSharedList };
const getShopifyLocationRpcSchema = { getShopifyLocation };
const customerProductListSchema = {
  getCustomerProductList,
  createCustomerProductList,
};
const getSubmissionWorkflowStatusSchema = { getSubmissionWorkflowStatus };
const getWarrantyEligibleProductsSchema = { getWarrantyEligibleProducts };
const getWarrantyRegistrationFlowSchema = { getWarrantyRegistrationFlow };
const getWarrantyRegistrationSchema = { getWarrantyRegistration };
const getRegistrationDiscountsSchema = { getRegistrationDiscounts };
const getMarketingTriggeredSchema = { getMarketingTriggered };
const submitWarrantyRegistrationSchema = { submitWarrantyRegistration };
const getOrderIdSchema = { getOrderId };
const setProvisionTypeRpcSchema = { setProvisionType };
const setExchangeShippingOptionRpcSchema = { setExchangeShippingOption };
const getReturnRpcSchema = { getReturn };
const getReturnTotalsRpcSchema = { getReturnTotals };
const getReturnTotalsFeatureFlagSchema = { getReturnTotalsFeatureFlag };
const marketingSchema = {
  getSignupForm,
  submitSignupFormStep,
  logSignupFormAnalytics,
  handleMarketingEmailUnsubscribe,
  getCustomFieldOptions,
  checkCustomerSubscription,
  createSubscription,
  getSubscriptionPlans,
};
const sendEmailAuthLinkSchema = { sendEmailAuthLink };
const setThirdPartyRefundProviderSchema = { setThirdPartyRefundProvider };
const singleOrderAuthSchema = { singleOrderAuth };
const orderlessAuthSchema = { orderlessAuth };
const convertTokenToCustomerAuthSchema = { convertTokenToCustomerAuth };
const convertBoxlessTokenToCustomerAuthSchema = {
  convertBoxlessTokenToCustomerAuth,
};
const convertSocialAuthToCustomerAuthSchema = {
  convertSocialAuthToCustomerAuth,
};
const ordersRpcs = { getOrders, hasReturnableItems, getOrdersFromSearch };
const isShopAppDisabledRpcSchema = { isShopAppDisabled };
const updatePickupVariantSchema = { updatePickupVariant };
const getProductSchema = { getProduct };
const getProductFilterByIdSchema = { getProductFilterById };
const getAiReturnReasonsForProductSchema = { getAiReturnReasonsForProduct };
const getReturnCreationStatusSchema = { getReturnCreationStatus };
const getRegistrationsSchema = { getRegistrations };
const getOrderRegistrationEligibilitySchema = {
  getOrderRegistrationEligibility,
};
const getOrderByIdSchema = { getOrderById };

const getTnplContractForOrderSchema = { getTnplContractForOrder };
const giftAuthSchema = { giftAuth };
const giftReceiptAuthSchema = { giftReceiptAuth };
const changeResetPointSchema = { changeResetPoint };
const getExchangeInvoiceSchema = { getExchangeInvoice };
const hasMultipleOrdersSchema = { hasMultipleOrders };
const hasRefundidIntegrationSchema = { hasRefundidIntegration };
const addMerchantOverridesSchema = { addMerchantOverrides };
const addCustomerDiscountCodesSchema = { addCustomerDiscountCodes };
const createManualDiscountSchema = { createManualDiscount };
const getReturnsByOrderIdSchema = { getReturnsByOrderId };
const addressAutofillSchema = {
  getAutocompleteSuggestions,
  getAddressFromSuggestion,
};
const cloneDraftReturnExcludingItemsSchema = { cloneDraftReturnExcludingItems };

const omsSchema = { getFulfillmentGroupAddress, updateFulfillmentGroupAddress };

const giftReceipts = {
  getGiftReceiptCount,
  createGiftReceipt,
  getGiftReceipt,
  getGiftReceipts,
  deleteGiftReceipt,
  sendGiftReceipt,
  createGiftReceiptRequest,
};

const exchangeConversion = {
  calculateNewOrderFromReturn,
  calculateReturnTotalsForReturn,
  getCartDetails,
  completeExchangeConversion,
};

const exchangeAgent = {
  getAdvancedExchangeRecommendation,
  getLocationFilteredInventory,
  getMerchantRecommendedExchangeProducts,
  getVariantExchangeRecommendations,
  getPersonalizedProductExchangeRecommendations,
  getPersonalizedRecommendationsStream,
  warmExchangeAgentCaches,
  warmExchangeAgentCachesV2,
  getReturnReasonRecommendationsStream,
};

const getExchangeShippingOptionsSchema = { getExchangeShippingOptions };
const getTrackingPageSchema = { getTrackingPage };
const getTrackingPageOrderSchema = { getTrackingPageOrder };
const generateDiscountCodeSchema = { generateDiscountCode };
const subscribeToTrackingUpdatesSchema = { subscribeToTrackingUpdates };
const classifyClaimImageSchema = { classifyClaimImage };
const getAllowedOrderEditTypesSchema = { getAllowedOrderEditTypes };
const mobileUploadSchema = {
  createMobileUploadToken,
  getMobileUploadResult,
  addMobileUploadImage,
  completeMobileUpload,
};
const orderEditingSchema = {
  cancelOrderSelfServe,
  updateOrderSelfServe,
  updateAddressSelfServe,
  updateContactInfoSelfServe,
  updateDiscountCodeSelfServe,
  getBestSellers,
  lookupProductsByIds,
  searchProducts,
};

export const rpcDefinition = {
  ...addressAutofillSchema,
  ...addAiReturnReasonInputRpcSchema,
  ...addCustomerDiscountCodesSchema,
  ...addDynamicMerchantItemFlowRpcSchema,
  ...addMerchantOverridesSchema,
  ...createManualDiscountSchema,
  ...addNewItemsRpcSchema,
  ...addReturnItemRpcSchema,
  ...calculateReturnTotalsRpcSchema,
  ...cancelPickupSchema,
  ...cancelReturnSchema,
  ...updateReturnExchangeAddressSchema,
  ...reschedulePickupSchema,
  ...changeAddressRpcSchema,
  ...changeResetPointSchema,
  ...convertSocialAuthToCustomerAuthSchema,
  ...convertTokenToCustomerAuthSchema,
  ...convertBoxlessTokenToCustomerAuthSchema,
  ...createDraftReturnRpcSchema,
  ...customerProductListSchema,
  ...exchangeConversion,
  ...editPendingItemQuantityRpcSchema,
  ...editReturnableItemRpcSchema,
  ...selectReturnItemsSchema,
  ...getBigCommerceUpsellRecommendedProductsRpcSchema,
  ...getCommerceCloudUpsellRecommendedProductsRpcSchema,
  ...searchExchangeProductsRpcSchema,
  ...getDraftReturnRpcSchema,
  ...getExchangeInvoiceSchema,
  ...getExchangeShippingOptionsSchema,
  ...getFaqSchema,
  ...getLocationsRpcSchema,
  ...getBlueYonderLocationsRpcSchema,
  ...getCarrierLocationsRpcSchema,
  ...getDropoffLocationsSchema,
  ...getReturnBearLocationsSchema,
  ...getMarketingTriggeredSchema,
  ...getOrderByIdSchema,
  ...getOrderIdSchema,
  ...getOrderRegistrationEligibilitySchema,
  ...getProductSchema,
  ...getProductFilterByIdSchema,
  ...getAiReturnReasonsForProductSchema,
  ...getRecommendedProductsByProviderRpcSchema,
  ...getRegistrationDiscountsSchema,
  ...getRegistrationsSchema,
  ...getReturnCreationStatusSchema,
  ...getReturnRpcSchema,
  ...getReturnTotalsRpcSchema,
  ...getReturnTotalsFeatureFlagSchema,
  ...getReturnsByOrderIdSchema,
  ...getSatisfactionResponseRpcSchema,
  ...getSharedListRpcSchema,
  ...getShopifyLocationRpcSchema,
  ...getShippingEstimateRpcSchema,
  ...getStripePublicKeyRpcSchema,
  ...getSubmissionWorkflowStatusSchema,
  ...getTnplContractForOrderSchema,
  ...getTrackingPageSchema,
  ...getTrackingPageOrderSchema,
  ...getWarrantyEligibleProductsSchema,
  ...getWarrantyRegistrationFlowSchema,
  ...getWarrantyRegistrationSchema,
  ...giftAuthSchema,
  ...giftReceiptAuthSchema,
  ...giftReceipts,
  ...hasMultipleOrdersSchema,
  ...hasRefundidIntegrationSchema,
  ...isShopAppDisabledRpcSchema,
  ...marketingSchema,
  ...omsSchema,
  ...orderlessAuthSchema,
  ...ordersRpcs,
  ...rejectReturnItemsRpcSchema,
  ...removeNewItemsRpcSchema,
  ...removeReturnItemRpcSchema,
  ...saveSatisfactionResponseSchema,
  ...sendEmailAuthLinkSchema,
  ...setCompensationMethodRpcSchema,
  ...setProvisionTypeRpcSchema,
  ...setExchangeShippingOptionRpcSchema,
  ...setShipmentMethodInfoRpcSchema,
  ...shopAppTempLoginSchema,
  ...singleOrderAuthSchema,
  ...skipAddReturnItemsRpcSchema,
  ...submitDraftReturnRpcSchema,
  ...subscribeToTrackingUpdatesSchema,
  ...submitWarrantyRegistrationSchema,
  ...setThirdPartyRefundProviderSchema,
  ...updatePickupVariantSchema,
  ...getShippingMethodsSchema,
  ...verifyAddressRpcSchema,
  ...generateDiscountCodeSchema,
  ...exchangeAgent,
  ...getAllowedOrderEditTypesSchema,
  ...classifyClaimImageSchema,
  ...cloneDraftReturnExcludingItemsSchema,
  ...orderEditingSchema,
  ...mobileUploadSchema,
};

export type CustomerPortalRpcDefinition = InferRpcDefinition<
  typeof rpcDefinition
>;
